import { useEffect, useRef, useState } from 'react';
import { turnFlagsFor } from './turnFlags';

const POLL_INTERVAL_MS = 1500;
const GENERIC_ERROR = 'Something went wrong.';

/**
 * All the session/polling/send/upload/clear state behind the public embed widget
 * (client/src/pages/EmbedChat.jsx) — split out the same way useAgentChat.js is split
 * out from ChatPanel.jsx, so EmbedChat.jsx stays a thin presentational wrapper. Plain
 * fetch() throughout, not useAgentChat.js's authenticated fetchApi wrapper — this
 * surface has no cookies/session at all, only the key+agentKey query params.
 */
export function useEmbedChat({ key, agentKey, identity = null }) {
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [agent, setAgent] = useState(null);
  // Mirrors sessionIdRef as state. The ref is what send()/upload() read synchronously; this
  // is what the host page is told about (AgentWidget.getSession), which needs a re-render.
  const [sessionId, setSessionId] = useState(null);
  // The last failure worth telling the HOST page about, so an integrator's on('error')
  // hears about a dead key or a failed turn instead of the widget just sitting there.
  const [lastError, setLastError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [pendingJob, setPendingJob] = useState(null); // {id, status, question, clientCalls} | null
  const [branding, setBranding] = useState(null); // per-key white-label config from /resolve
  // Auto Approve toggle (same contract as useAgentChat.js's approvalMode): 'attended'
  // pauses for the visitor on ask/confirm and denies gated; 'auto' lets the Human Proxy
  // decide non-destructive gated calls and answer ask_user from context instead.
  const [approvalMode, setApprovalMode] = useState('attended');
  const [endUser, setEndUser] = useState(null); // verified identity echo ({name, email}) from /resolve
  const [feedback, setFeedback] = useState({}); // messageId -> {rating, comment}
  const [hostOrigin, setHostOrigin] = useState(null); // the allow-listed embedding origin, from /resolve
  const sessionIdRef = useRef(null);
  const pendingJobRef = useRef(null); // mirror of pendingJob for answerQuestion() (avoids a stale closure)
  const pollTimerRef = useRef(null);
  // Client-provided tools the host page currently offers, set over the postMessage bridge
  // (client/public/embed.js). Refs, not state: they are read at send time, and a page
  // registering a handler should not re-render the thread.
  const clientToolsTokenRef = useRef(null);
  const pageContextRef = useRef(null);
  // Identified users get their OWN stored session (per-user history) — an anonymous
  // visitor on the same browser never resumes an identified session and vice versa.
  const storageKey = `embedChatSession:${key}:${agentKey}:${identity ? 'id' : 'anon'}`;
  const idQS = identity ? `&identity=${encodeURIComponent(identity)}` : '';

  const fileHrefFor = (fileId) => `/api/embed/files/${fileId}?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}${idQS}`;
  const statusUrl = (sessionId) =>
    `/api/embed/session/${sessionId}/status?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}${idQS}`;

  const stopPolling = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  const refreshSession = async (sessionId) => {
    const res = await fetch(statusUrl(sessionId));
    if (!res.ok) throw new Error('status_unavailable');
    const data = await res.json();
    // `id` keys per-message feedback and `toolCallCount` sizes the tool-trace badge. Both
    // come straight off the shared status payload (services/chat/sessionStatus.js); dropping
    // them here is why the embed surface had neither thumbs nor a trace.
    setMessages(data.messages.map((row) => ({
      id: row.id, role: row.role, content: row.content,
      toolCallCount: row.toolCallCount || 0, file: row.file || null,
    })));
    setPendingJob(data.pendingJob || null);
    pendingJobRef.current = data.pendingJob || null;
    const isPending = !!data.pendingJob;
    setSending(isPending);
    return isPending;
  };

  const pollUntilDone = (sessionId) => {
    stopPolling();
    const tick = async () => {
      let stillPending = true;
      try {
        stillPending = await refreshSession(sessionId);
      } catch {
        // transient — keep polling rather than giving up on one failed tick
      }
      if (stillPending) pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    sessionIdRef.current = localStorage.getItem(storageKey) || null;
    setSessionId(sessionIdRef.current);
    if (!key || !agentKey) {
      setStatus('unavailable');
      setLastError({ message: 'Missing embed key or agent key.', code: 'missing_key' });
      return undefined;
    }
    fetch(`/api/embed/resolve?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}${idQS}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        setAgent(data.agent);
        setBranding(data.branding || null);
        setEndUser(data.end_user || null);
        setHostOrigin(data.host_origin || null);
        setStatus('ready');
        if (sessionIdRef.current) {
          loadFeedback(sessionIdRef.current);
          refreshSession(sessionIdRef.current)
            .then((isPending) => { if (isPending) pollUntilDone(sessionIdRef.current); })
            // stale/foreign session — start fresh
            .catch(() => { sessionIdRef.current = null; setSessionId(null); });
        }
      })
      .catch((err) => {
        setStatus('unavailable');
        setLastError({ message: 'This chat could not be loaded.', code: err?.status ? `http_${err.status}` : 'resolve_failed' });
      });
    return stopPolling;
  }, []);

  /** Mirrors useAgentChat.js's uploadAttachment — same {fileId, filename, mimeType,
   *  preview} contract Composer expects. */
  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', key);
    formData.append('agentKey', agentKey);
    if (identity) formData.append('identity', identity);
    if (sessionIdRef.current) formData.append('sessionId', sessionIdRef.current);
    const res = await fetch('/api/embed/attachments', { method: 'POST', body: formData });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Upload failed');
    const data = await res.json();
    sessionIdRef.current = data.sessionId;
    setSessionId(data.sessionId);
    localStorage.setItem(storageKey, String(data.sessionId));
    return { fileId: data.fileId, filename: data.filename, mimeType: data.mimeType, preview: data.preview };
  };

  const send = async (text, attachment, turnOpts) => {
    const trimmed = text.trim();
    if ((!trimmed && !attachment) || sending) return;
    setSending(true);
    const optimisticFile = attachment ? { id: attachment.fileId, filename: attachment.filename, mimeType: attachment.mimeType, preview: attachment.preview } : null;
    setMessages((m) => [...m, { role: 'user', content: trimmed, file: optimisticFile }]);
    try {
      const res = await fetch('/api/embed/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key, agentKey, identity: identity || undefined, message: trimmed,
          sessionId: sessionIdRef.current, attachmentFileId: attachment?.fileId,
          approvalMode,
          // Per-turn voice-mode overrides (useVoiceMode's consumePendingPatch) — same
          // shape useAgentChat.js spreads into /api/agent/chat; the embed route already
          // forwards them into chatEngine.submitTurn (routes/embedRouter.js).
          ...turnFlagsFor(turnOpts),
          // Re-sent per turn, not once per session: in a single-page app the tools the
          // page offers change as the visitor navigates.
          clientToolsToken: clientToolsTokenRef.current || undefined,
          pageContext: pageContextRef.current || undefined,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Request failed');
      const data = await res.json();
      sessionIdRef.current = data.sessionId;
      setSessionId(data.sessionId);
      localStorage.setItem(storageKey, String(data.sessionId));
      pollUntilDone(data.sessionId);
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', content: err.message || GENERIC_ERROR }]);
      setLastError({ message: err.message || GENERIC_ERROR, code: 'send_failed' });
      setSending(false);
    }
  };

  /** Same "abandon the session client-side, no data deleted" behavior as
   *  useAgentChat.js's clearChat — see ClearChatButton.jsx's own comment. */
  const clearChat = () => {
    stopPolling();
    localStorage.removeItem(storageKey);
    sessionIdRef.current = null;
    setSessionId(null);
    pendingJobRef.current = null;
    setMessages([]);
    setSending(false);
    setPendingJob(null);
    setFeedback({});
  };

  /** Answers a paused ask_user question (services/chat/tools/index.js) — resumes the same
   *  turn server-side; polling picks the result back up exactly like a fresh send(). Same
   *  contract as useAgentChat.js's answerQuestion, over the public embed endpoint. */
  const answerQuestion = async ({ selectedValue, freeText }) => {
    const sessionId = sessionIdRef.current;
    const job = pendingJobRef.current;
    if (!job?.id || !sessionId) return;
    try {
      const res = await fetch(`/api/embed/session/${sessionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, agentKey, identity: identity || undefined, jobId: job.id, selectedValue, freeText }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Request failed');
      pollUntilDone(sessionId);
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', content: err.message || GENERIC_ERROR }]);
      setLastError({ message: err.message || GENERIC_ERROR, code: 'answer_failed' });
    }
  };

  /** Posts the page's tool results back, resuming the same turn — the client-tool
   *  counterpart to answerQuestion(). Polling picks the continuation up exactly as it does
   *  after a fresh send(). */
  const submitToolResults = async (results) => {
    const sessionId = sessionIdRef.current;
    const job = pendingJobRef.current;
    if (!job?.id || !sessionId || !results.length) return;
    try {
      const res = await fetch(`/api/embed/session/${sessionId}/tool-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, agentKey, identity: identity || undefined, jobId: job.id, results }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Request failed');
      pollUntilDone(sessionId);
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', content: err.message || GENERIC_ERROR }]);
      setLastError({ message: err.message || GENERIC_ERROR, code: 'tool_result_failed' });
    }
  };

  /** Which tools one settled answer ran. The response is redacted server-side to names,
   *  status and timing — see the traces route in routes/embedRouter.js. */
  const fetchTraces = async ({ sessionId: id, messageId }) => {
    const res = await fetch(`/api/embed/session/${id}/traces?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}&messageId=${encodeURIComponent(messageId)}${idQS}`);
    if (!res.ok) throw new Error('traces_unavailable');
    return res.json();
  };

  /** Past votes for the resumed session, so thumbs render filled rather than empty after a
   *  reload. Silent on failure: feedback is an extra, never a reason to break the thread. */
  const loadFeedback = async (id) => {
    try {
      const res = await fetch(`/api/embed/session/${id}/feedback?key=${encodeURIComponent(key)}&agentKey=${encodeURIComponent(agentKey)}${idQS}`);
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      (data.feedback || []).forEach((f) => { map[f.message_id] = { rating: f.rating, comment: f.comment }; });
      setFeedback(map);
    } catch {
      // no votes rendered is a strictly better failure than no conversation
    }
  };

  const postFeedback = (messageId, rating, comment) => fetch(`/api/embed/session/${sessionIdRef.current}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, agentKey, identity: identity || undefined, messageId, rating, comment }),
  });

  /** Optimistic with no rollback, same stance as the internal chat (ChatPanel.jsx): a lost
   *  vote is not worth showing the visitor an error about. */
  const voteFeedback = async (messageId, rating) => {
    if (!sessionIdRef.current) return;
    setFeedback((m) => {
      if (rating) return { ...m, [messageId]: { rating, comment: m[messageId]?.comment ?? null } };
      const next = { ...m };
      delete next[messageId];
      return next;
    });
    try { await postFeedback(messageId, rating); } catch { /* ignore */ }
  };

  const commentFeedback = async (messageId, comment) => {
    if (!sessionIdRef.current) return;
    const rating = feedback[messageId]?.rating || 'down';
    setFeedback((m) => ({ ...m, [messageId]: { rating, comment } }));
    try { await postFeedback(messageId, rating, comment); } catch { /* ignore */ }
  };

  /** Called by the postMessage bridge when the host page publishes what it offers. */
  const setClientTools = (token) => { clientToolsTokenRef.current = token || null; };
  const setPageContext = (context) => {
    pageContextRef.current = context ? JSON.stringify(context) : null;
  };

  return {
    status, agent, branding, endUser, hostOrigin, messages, sending, pendingJob,
    sessionId, lastError, feedback, voteFeedback, commentFeedback, fetchTraces,
    approvalMode, setApprovalMode,
    send, answerQuestion, submitToolResults, setClientTools, setPageContext,
    uploadAttachment, clearChat, fileHrefFor,
  };
}

export default useEmbedChat;
